import { Injectable, NgModule } from '@angular/core'
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterModule,
  Routes,
} from '@angular/router'
import {
  FeatureToggleModule,
  NgxFeatureToggleRouteGuard,
} from 'ngx-feature-toggle'

/* Auth & Layouts */
import { AuthGuard } from './auth/auth-guard.service'
import { FooterOnlyLayoutComponent } from './layouts/footer-only-layout/footer-only-layout.component'
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component'
/* Hunt guards & redirect */
import { HuntRoutes } from './modules/hunt/hunt.routing'
import { IframeGuard } from './modules/shared/guards/iframe.guard'
/* Other screens */
import { FourOhFourComponent } from './screens/404/404.component'
import { ContactUsComponent } from './screens/customer-support/contact.component'
import { HuntGateComponent } from './screens/hunt-gate/hunt-gate.component'
import { LoginComponent } from './screens/portal/login/login.component'
import { NewPasswordComponent } from './screens/portal/new-password/new-password.component'
import { PortalComponent } from './screens/portal/portal.component'
import { ResetPasswordComponent } from './screens/portal/reset-password/reset-password.component'
import { PremiumGateComponent } from './screens/premium-gate/premium-gate.component'
import { PrivacyComponent } from './screens/privacy-policy/privacy.component'
import { TrialGateComponent } from './screens/trial-gate/trial-gate.component'
/** Simple redirect for external documents **/
@Injectable({ providedIn: 'root' })
export class RedirectGuard implements CanActivate {
  canActivate(route: ActivatedRouteSnapshot): boolean {
    window.location.href = route.data['externalUrl']
    return true
  }
}

const routes: Routes = [
  // —————————————————————————————————————————————
  // Main site (header + footer)
  // —————————————————————————————————————————————
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      // research
      {
        path: 'research',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/research/research.module').then(
            (m) => m.ResearchModule,
          ),
      },
      { path: '', redirectTo: 'research', pathMatch: 'full' },

      // settings
      {
        path: 'settings',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./modules/settings/settings.module').then(
            (m) => m.SettingsModule,
          ),
      },

      // hunt (eager-loaded)
      {
        path: 'hunt',
        canActivate: [AuthGuard, NgxFeatureToggleRouteGuard],
        runGuardsAndResolvers: 'always',

        data: { featureToggle: ['huntModule'], redirectTo: '/hunt-gate' },
        children: HuntRoutes,
      },

      // releases
      {
        path: 'releases',
        canActivate: [AuthGuard],
        loadChildren: () =>
          import('./changelog/release.module').then((m) => m.ReleaseModule),
      },

      // misc
      { path: 'customer-support', component: ContactUsComponent },
      { path: 'trial-gate', component: TrialGateComponent },
      { path: 'premium-gate', component: PremiumGateComponent },
      { path: 'hunt-gate', component: HuntGateComponent },
      { path: '404', component: FourOhFourComponent },

      // ** all `/list/...` legacy routes have been removed **
    ],
  },

  // —————————————————————————————————————————————
  // Footer‐only pages (no header)
  // —————————————————————————————————————————————
  {
    path: '',
    component: FooterOnlyLayoutComponent,
    children: [
      {
        path: 'login',
        component: PortalComponent,
        canActivate: [IframeGuard],
        children: [{ path: '', component: LoginComponent }],
      },
      {
        path: 'new-password',
        component: PortalComponent,
        canActivate: [IframeGuard],
        children: [{ path: '', component: NewPasswordComponent }],
      },
      {
        path: 'reset-password',
        component: PortalComponent,
        canActivate: [IframeGuard],
        children: [{ path: '', component: ResetPasswordComponent }],
      },
      { path: 'privacy-policy', component: PrivacyComponent },
    ],
  },

  // —————————————————————————————————————————————
  // External PDF redirects
  // —————————————————————————————————————————————
  {
    path: 'terms-and-conditions',
    component: RedirectGuard,
    canActivate: [RedirectGuard],
    data: {
      externalUrl:
        '/assets/docs/terms-and-conditions/hunter-terms-and-conditions.pdf',
    },
  },
  {
    path: 'community-terms',
    component: RedirectGuard,
    canActivate: [RedirectGuard],
    data: {
      externalUrl:
        '/assets/docs/terms-and-conditions/hunter-community-terms.pdf',
    },
  },
  {
    path: 'eval-agreement',
    component: RedirectGuard,
    canActivate: [RedirectGuard],
    data: {
      externalUrl:
        '/assets/docs/terms-and-conditions/hunter-evaluation-terms.pdf',
    },
  },

  // catch‐all
  { path: '**', redirectTo: '/404' },
]

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'disabled',
    }),
    FeatureToggleModule,
  ],
  exports: [RouterModule],
  providers: [
    {
      provide: 'externalUrlRedirectResolver',
      useValue: (route: ActivatedRouteSnapshot): void => {
        window.location.href = route.data.externalUrl
      },
    },
  ],
})
export class AppRoutingModule {}
